import { apiFetch } from "@/lib/api";

import type { ClampQcWorkOrderBundle } from "./clampQcMockData";

export const CLAMPQC_HUB_WORK_ORDERS_QUERY_KEY = ["clamp-qc", "work-orders"] as const;

export type ClampQcHubJobStatus = ClampQcWorkOrderBundle["status"];

function hubStatusLooksCancelled(hubStatus: string | null | undefined): boolean {
  const lower = (hubStatus ?? "").trim().toLowerCase();
  return lower === "cancelled" || lower === "canceled";
}

/** Hover hint for the status pill (hub-native parent builds use hub_status, not Megaventory). */
export function clampQcWorkOrderStatusHoverHintText(input: {
  hubStatus?: string | null;
  executionMode?: string | null;
  mvStatus?: string | null;
}): string {
  const mode = (input.executionMode ?? "").trim();
  const hubLine =
    input.hubStatus == null
      ? "hub_status: (null)"
      : input.hubStatus.trim() === ""
        ? "hub_status: (blank)"
        : `hub_status: ${input.hubStatus.trim()}`;
  if (mode === "hub_native") {
    return `${hubLine} (Suntronic parent work order; not a Megaventory work order)`;
  }
  const mv = input.mvStatus;
  const mvLine =
    mv == null
      ? "mv_status: (null)"
      : String(mv).trim() === ""
        ? "mv_status: (blank)"
        : `mv_status: ${String(mv).trim()}`;
  return `${hubLine}\n${mvLine}`;
}

/** @deprecated Use clampQcWorkOrderStatusHoverHintText. */
export function clampQcMvStatusSupabaseHoverHintText(mvStatus: string | null | undefined): string {
  return clampQcWorkOrderStatusHoverHintText({ mvStatus, hubStatus: null });
}

export function clampQcWorkOrderStatusChipLabel(
  status: ClampQcHubJobStatus,
  hubStatus?: string | null,
): string {
  if (hubStatusLooksCancelled(hubStatus)) {
    return "Cancelled";
  }
  switch (status) {
    case "active":
      return "Active";
    case "on_hold":
      return "On hold";
    case "complete":
      return "Complete";
    default:
      return status;
  }
}

export function clampQcWorkOrderStatusChipClassName(
  status: ClampQcHubJobStatus,
  hubStatus?: string | null,
): string {
  if (hubStatusLooksCancelled(hubStatus)) {
    return "bg-rose-50 text-rose-900 ring-rose-200";
  }
  switch (status) {
    case "active":
      return "bg-emerald-50 text-emerald-800 ring-emerald-200";
    case "on_hold":
      return "bg-amber-50 text-amber-900 ring-amber-200";
    case "complete":
      return "bg-slate-100 text-slate-700 ring-slate-200";
    default:
      return "bg-slate-100 text-slate-700 ring-slate-200";
  }
}

/** Roll-up row for Clamp QC reporting (one row per QC work order). */
export type ClampQcReportingRollupRow = {
  slug: string;
  status: ClampQcWorkOrderBundle["status"];
  mvStatus: string | null;
  hubStatus?: string | null;
  executionMode?: string | null;
  mvId: number | null;
  mvNumber: string | null;
  workOrder: string;
  sku: string;
  locationId: number | null;
  locationCode: string;
  plannedQuantity: number;
  quantityComplete: number;
  failCount: number;
  avgLineHarvestDelta: number | null;
};

export interface ClampQcHubWorkOrderListItem {
  slug: string;
  buildOrderId: number;
  title: string;
  workOrder: string;
  mvId: number | null;
  mvNumber: string | null;
  mvStatus?: string | null;
  hubStatus?: string | null;
  executionMode?: string | null;
  orderNumber: string | null;
  status: ClampQcWorkOrderBundle["status"];
  sku: string;
  quantity: number;
  quantityComplete: number;
  failCount: number;
  avgLineHarvestDelta: number | null;
  mfrShipByDate: string;
  mfrReferenceNumber: string | null;
  locationId: number | null;
  locationCode: string;
}

export function clampQcReportingRollupRowsFromList(items: ClampQcHubWorkOrderListItem[]): ClampQcReportingRollupRow[] {
  return items.map((item) => ({
    slug: item.slug,
    status: item.status,
    mvStatus: item.mvStatus ?? null,
    hubStatus: item.hubStatus ?? null,
    executionMode: item.executionMode ?? null,
    mvId: item.mvId,
    mvNumber: item.mvNumber,
    workOrder: item.workOrder,
    sku: item.sku,
    locationId: item.locationId,
    locationCode: item.locationCode,
    plannedQuantity: item.quantity,
    quantityComplete: item.quantityComplete,
    failCount: item.failCount,
    avgLineHarvestDelta: item.avgLineHarvestDelta,
  }));
}

export async function fetchClampQcHubWorkOrders(): Promise<ClampQcHubWorkOrderListItem[]> {
  return apiFetch<ClampQcHubWorkOrderListItem[]>("/api/clamp-qc/work-orders");
}

export async function fetchClampQcHubWorkOrderBundle(slug: string): Promise<ClampQcWorkOrderBundle> {
  return apiFetch<ClampQcWorkOrderBundle>(
    `/api/clamp-qc/work-orders/${encodeURIComponent(slug)}`,
  );
}

export type ClampQcMostRecentSerialPayload = {
  serial: string | null;
  recordedAtIso: string | null;
};

export async function fetchClampQcMostRecentSerial(slug: string): Promise<ClampQcMostRecentSerialPayload> {
  return apiFetch<ClampQcMostRecentSerialPayload>(
    `/api/clamp-qc/work-orders/${encodeURIComponent(slug)}/most-recent-serial`,
  );
}
